import { useState, useCallback, useRef } from "react";
import { Request } from "../../../services/Request";
import { urls } from "../../../Utils/Urls";
import { getFarmerIdentity } from "./farmerIdentity";

// Per-farmer recent-search history, backed by dfs-personalization's
// /recent-search/v1 endpoints (RecentSearchController). Identity comes from the
// citizen session token (DfsWeb.access-token, injected by Request() as
// RequestInfo.authToken), which the API gateway validates and resolves to
// userInfo. Verified on QA: a tokenless request is rejected 401 at the gateway
// (and an AGENT token 403s) BEFORE reaching the service, so the service's
// RecentSearchCriteria fallback is never actually exercised on web — we still
// send the criteria as a harmless, explicit identity hint.
//
// Recording is NOT done here — it is a side-effect of the v3 global search
// (SearchService.saveFromSearch), fired at commit time by recordCommittedSearch
// (see recordSearch.js). See .agent/globalSearch/README.md and dfs_recent_search.

const RECENT_LIMIT = 5;

const requestInfo = (action, msgId) => ({
  apiId: "dfs-web",
  ver: "1.0",
  action,
  msgId,
});

// `module` is a forward-compatible scope hint: the backend ignores it until
// dfs_recent_search becomes module-aware, so history stays shared today.
const recentCriteria = (identity, module, extra) => ({
  tenantId: identity.tenantId,
  userUuid: identity.uuid,
  ...(module ? { module } : {}),
  ...extra,
});

export const useRecentSearches = ({ module } = {}) => {
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  // Guards against overlapping fetches (focus can fire the effect repeatedly).
  const inFlight = useRef(false);

  const isLoggedIn = getFarmerIdentity() !== null;

  const fetchRecent = useCallback(async () => {
    const identity = getFarmerIdentity();
    if (!identity || inFlight.current) return;

    inFlight.current = true;
    setLoading(true);
    try {
      const response = await Request({
        url: urls.recent_search,
        data: {
          RecentSearchCriteria: recentCriteria(identity, module, { limit: RECENT_LIMIT }),
          RequestInfo: requestInfo("search", "recent-search"),
        },
      });
      // Request() resolves the error response on failure, so guard on status —
      // otherwise a 4xx/5xx would masquerade as "no history" and blank the list.
      const ok = response?.status >= 200 && response?.status < 300;
      const list = ok ? response?.data?.recentSearches : null;
      if (Array.isArray(list)) setRecentSearches(list);
    } catch (err) {
      // Best-effort: leave the current list untouched on failure.
      console.error("Recent search fetch error:", err);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [module]);

  const deleteOne = useCallback(async (searchId) => {
    const identity = getFarmerIdentity();
    if (!identity || !searchId) return;

    // Optimistic removal; a later fetchRecent() reconciles if the call fails.
    setRecentSearches((prev) => prev.filter((item) => item.id !== searchId));
    try {
      await Request({
        url: urls.recent_search_delete,
        data: {
          searchId,
          RecentSearchCriteria: recentCriteria(identity, module),
          RequestInfo: requestInfo("update", "recent-search-delete"),
        },
      });
    } catch (err) {
      console.error("Recent search delete error:", err);
    }
  }, [module]);

  const clearAll = useCallback(async () => {
    const identity = getFarmerIdentity();
    if (!identity) return;

    setRecentSearches([]);
    try {
      await Request({
        url: urls.recent_search_clear,
        data: {
          RecentSearchCriteria: recentCriteria(identity, module),
          RequestInfo: requestInfo("update", "recent-search-clear"),
        },
      });
    } catch (err) {
      console.error("Recent search clear error:", err);
    }
  }, [module]);

  return { recentSearches, loading, isLoggedIn, fetchRecent, deleteOne, clearAll };
};
